import {Flex} from 'src/design/atoms/Flex';
import React, {useState} from 'react';
import {useParams} from 'react-router-dom';
import {PathUtility} from 'src/shared/constants/PathBuilder';

import useSetDocumentTitle from 'src/shared/hooks/useSetDocumentTitle';
import {capitalize} from 'src/shared/utils/formatters';

import {
  GetUrlSectionFromTaskType,
  taskRowSubText,
} from 'src/app/tasks/taskHelpers';

import {
  FeatureFlagEnum,
  ModuleEnum,
  NoteModuleEnum,
  NotificationModuleEnum,
  TaskTypeEnum,
} from 'src/api/ProactApiClient';
import {getAssignedFormTransitionUrl} from 'src/app/assigned-forms/AssignedForm';
import {FormTransitionBar} from 'src/app/forms/FormTransitionBar';
import {TasksOfflineBoundary} from 'src/app/tasks/TasksOfflineBoundary';
import {TasksPendingSyncBanner} from 'src/app/tasks/offline/TasksPendingSyncBanner';
import {TaskAssignmentSupportingSections} from 'src/app/tasks/TaskAssignmentSupportingSections';
import {useTaskAssignmentView} from 'src/app/tasks/useTaskAssignmentView';
import {TaskFeatureAssignmentView} from 'src/app/tasks/TaskFeatureViews';
import {BackBar} from 'src/app/layout/BackBar';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {SplitPane} from 'src/design/atoms/SplitPane';
import {NotesModule} from 'src/app/notes/NotesModule';
import {NotificationsModule} from 'src/app/notifications/NotificationsModule';
import {TableBodyRender} from 'src/design/molecules/TableBodyRender';
import {ErrorAlert} from 'src/design/molecules/ErrorAlert';
import {Text} from 'src/design/atoms/Text';

import {routeRegistry} from 'src/pages/RouteRegistry';
import {useTaskPermissions} from 'src/shared/auth/permissions/useTaskPermissions';
import {useUrlQueryTabV2} from 'src/shared/hooks/useUrlQueryTabV2';
import {useTaskBreadcrumbs} from 'src/shared/hooks/useTaskBreadcrumbs';
import {useBackNavigation} from 'src/shared/hooks/useBackNavigation';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';

export type TaskAssignmentViewType = 'content' | 'notes' | 'notifications' | '';

export interface ViewUserTaskPageProps {
  taskType: TaskTypeEnum;
  featureFlag: FeatureFlagEnum;
  module: ModuleEnum;
}

const ViewUserTaskPage: React.FC<ViewUserTaskPageProps> = ({
  taskType,
  featureFlag,
  module,
}) => {
  const {assignmentId} = useParams();
  const [view, setView] = useState<TaskAssignmentViewType>('content');

  const taskPerms = useTaskPermissions(module);

  const parsedAssignmentId =
    assignmentId !== undefined && !isNaN(+assignmentId)
      ? +assignmentId
      : undefined;

  const {
    viewItem,
    isLoading,
    error: assignmentError,
    refetch: refetchAssignment,
    machine,
    pills,
    postTransition,
  } = useTaskAssignmentView(parsedAssignmentId);

  useSetDocumentTitle(capitalize(GetUrlSectionFromTaskType(taskType)));

  const tasksListPath = PathUtility.TasksDashboard(taskType, 'user');
  const {returnTo} = useBackNavigation(tasksListPath);
  const handleOfflineQueued = (): void => returnTo(tasksListPath);

  const transitionFormUrl = getAssignedFormTransitionUrl(
    viewItem?.taskDefinition.formDefinitionId as number,
    assignmentId as string,
    viewItem?.formSubmissionId,
    viewItem?.formSubmissionState
  );

  const breadcrumbs = useTaskBreadcrumbs({
    taskType,
    mode: 'user',
    itemSubject: viewItem?.taskDefinition?.subject,
  });

  const subText = viewItem ? taskRowSubText(viewItem) : '';
  const {RenderedTabsComponent} = useUrlQueryTabV2<TaskAssignmentViewType>({
    view: (view ?? '') as TaskAssignmentViewType,
    defaultView: 'content',
    tabs: [
      {
        view: 'content',
        label: 'Content',
        component: (
          <>
            <SplitPane
              pane={<TaskAssignmentSupportingSections viewItem={viewItem} />}>
              {viewItem && <TaskFeatureAssignmentView item={viewItem} />}

              {machine && viewItem && (
                <FormTransitionBar
                  stateMachine={machine}
                  stateId={viewItem.state}
                  transitionBaseUrl={`task-assignment/${assignmentId}/transition`}
                  postTransition={postTransition}
                  onOfflineQueued={handleOfflineQueued}
                  transitionFormUrl={transitionFormUrl}
                  objectType={viewItem.taskDefinition.taskType}
                  onRefreshTransition={refetchAssignment}
                  invalidateTagsOnComplete={['TaskAssignments']}
                />
              )}
            </SplitPane>
          </>
        ),
      },
      {
        view: 'notes',
        label: 'Notes',
        disabled: !taskPerms.notes,
        component: (
          <>
            <NotesModule
              entityId={viewItem?.id ?? 0}
              noteType={NoteModuleEnum.TaskAssignment}
            />
          </>
        ),
      },
      {
        view: 'notifications',
        label: 'Notifications',
        disabled: !taskPerms.notifications,
        component: (
          <>
            <NotificationsModule
              entityId={viewItem?.id ?? 0}
              notificationType={NotificationModuleEnum.TaskAssignment}
            />
          </>
        ),
      },
    ],
    onSelectView: view => setView(view as TaskAssignmentViewType),
  });

  return (
    <TasksOfflineBoundary>
      <ContentWrapper featureFlag={featureFlag}>
        <BackBar fallbackTo={tasksListPath} breadcrumbs={breadcrumbs} />
        {assignmentError ? (
          <ErrorAlert message={parseErrorMessage(assignmentError)} />
        ) : !isLoading && viewItem ? (
          <Flex direction="column" w="100%">
            <PageHeader>
              <PageHeader.Title>
                {viewItem.taskDefinition.subject || 'Item not found'}
              </PageHeader.Title>
              {pills.length > 0 && (
                <PageHeader.Badges>
                  {pills.map((pill, index) => (
                    <PageHeader.Badge
                      key={index}
                      color={pill.color}
                      variant="light">
                      {pill.text}
                    </PageHeader.Badge>
                  ))}
                </PageHeader.Badges>
              )}
              {subText && <PageHeader.Meta>{subText}</PageHeader.Meta>}
            </PageHeader>

            <TasksPendingSyncBanner />

            {RenderedTabsComponent}
          </Flex>
        ) : isLoading ? (
          <TableBodyRender isLoading />
        ) : (
          <Text c="dimmed">This assignment is no longer available.</Text>
        )}
      </ContentWrapper>
    </TasksOfflineBoundary>
  );
};

// Register the routes with the new system
[
  {
    type: TaskTypeEnum.Approval,
    featureFlag: FeatureFlagEnum.Approvals,
    module: ModuleEnum.Approvals,
  },
  {
    type: TaskTypeEnum.ToolboxTalk,
    featureFlag: FeatureFlagEnum.ToolboxTalks,
    module: ModuleEnum.ToolboxTalks,
  },
  {
    type: TaskTypeEnum.Form,
    featureFlag: FeatureFlagEnum.Forms,
    module: ModuleEnum.AssignedForms,
  },
].forEach(item => {
  routeRegistry.registerPageRoute({
    backNavigation: 'page',
    path: PathUtility.ViewTaskAssignment(item.type, ':assignmentId'),
    element: (
      <ViewUserTaskPage
        taskType={item.type}
        featureFlag={item.featureFlag}
        module={item.module}
      />
    ),
    isPrivate: true,
    supportsOffline: true,
  });
});

export default ViewUserTaskPage;
